import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Switch } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AuthenticatedTemplate, MsalAuthenticationTemplate, MsalProvider, useMsal } from "@azure/msal-react";
import { EventType, InteractionType, RedirectRequest } from "@azure/msal-browser";
import { useIdleTimer } from "react-idle-timer";
import { isIE } from "react-device-detect";

import "./App.scss";

import { useTypedSelector } from "./store/models/root.interface";
import { fetchSubscriptions } from "./store/actions/subscriptions.actions";
import { fetchReports } from "./store/actions/reports.actions";
import { fetchSettings } from "./store/actions/root.actions";
import Layout from "./views/Shared/Layout";
import Loader from "./common/components/Loader";
import AuthError from "./common/components/AuthError";
import Redirecting from "./common/components/Redirecting";
import msalInstance, { scopes, b2cPolicies } from "./auth";
import MatomoTracker from "./MatomoTracker";

const authRequest: RedirectRequest = {
  scopes,
};

const bypassAuth = process.env.REACT_APP_BYPASS_AUTH === "true";

const logoutMinutes = (() => {
  const value = parseInt(process.env.REACT_APP_LOGOUT_MINUTES ?? "0");
  if (isNaN(value)) {
    return 0;
  }
  return Math.max(value, 0);
})();

const Content = () => {

  const settings = useTypedSelector(state => state.root.settings);
  const subscriptions = useTypedSelector(state => state.subscriptions.items);
  const reports = useTypedSelector(state => state.reports);
  const [isLoaded, setIsLoaded] = useState(false);
  const dispatch = useDispatch();

  const handleOnIdle = async () => {
    if (!!logoutMinutes) {
      await msalInstance.logout();
    }
  }

  const { } = useIdleTimer({
    timeout: (logoutMinutes || 60 * 24) * 60 * 1000,
    onIdle: handleOnIdle,
    debounce: 500,
    crossTab: {
      emitOnAllTabs: true,
    },
  });

  useEffect(() => {
    if (subscriptions && subscriptions !== "loading" &&
      settings && settings !== "loading" &&
      !!reports) {
      setIsLoaded(true);
    }
  }, [subscriptions, settings]);

  useEffect(() => {
    dispatch(fetchSubscriptions());
    dispatch(fetchReports());
    dispatch(fetchSettings());
  }, []);

  return bypassAuth
    ? (
      <>
        {isLoaded
          ? (
            <Router>
              <Switch>
                <Layout />
              </Switch>
            </Router>)
          : (<Loader show />)}
      </>
    )
    : (
      <>
        <AuthenticatedTemplate>
          {isLoaded
            ? (
              <Router>
                <Switch>
                  <MatomoTracker>
                    <Layout />
                  </MatomoTracker>
                </Switch>
              </Router>)
            : (<Loader show />)}
        </AuthenticatedTemplate>
      </>
    );
};

const Authorize = () => {

  const { instance } = useMsal();
  const [[isError, errorMessage], setIsError] = useState([false, ""]);
  const [[isRedirecting, redirectMessage], setIsRedirecting] = useState([false, ""]);
  const [isHandledAuthError, handleAuthError] = useState(false);

  const redirectToAuth = async (request?: RedirectRequest) => {
    if (!isHandledAuthError) {
      handleAuthError(true);
      await instance.loginRedirect(request);
    }
  };

  const redirectToAuthWithMessage = (message: string) => {
    setIsRedirecting([true, message]);
    window.setTimeout(async () => {
      await instance.logout();
    }, 3000);
  }

  /**
   * Using the event API, you can register an event callback that will do something when an event is emitted.
   * When registering an event callback in a react component you will need to make sure you do 2 things.
   * 1) The callback is registered only once
   * 2) The callback is unregistered before the component unmounts.
   * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/events.md
   */
  useEffect(() => {

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length !== 0) {
      instance.setActiveAccount(accounts[0]);
    }

    const callbackId = instance.addEventCallback(async (event: any) => {

      if (event.eventType === EventType.LOGIN_FAILURE) {
        if (event.error) {
          if (event.error.errorMessage.indexOf("AADB2C90118") !== -1) {
            // User clicked "forgot password"
            await redirectToAuth(b2cPolicies.authorities.forgotPassword);
          }
          else if (event.error.errorMessage.indexOf("AADB2C90091") !== -1) {
            // User cancelled signup or password reset
            await redirectToAuth();
          }
          else if (event.error.errorMessage.indexOf("AADB2C: No matching profile found") !== -1) {
            // User has been authenticated by an IDP but have no matching CRM record
            setIsError([true, "You were recognised by the identity provider but no matching profile could be found."]);
          }
          else {
            // An unexpected error occurred
            redirectToAuthWithMessage("We need you to sign in again.");
            return;
          }
        }
      }

      if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        // We were unable to obtain a token - user must sign in again
        await redirectToAuth();
      }

      if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        if (event?.payload) {
          /**
           * We need to reject id tokens that were not issued with the default sign-in policy.
           * "acr" claim in the token tells us what policy is used (NOTE: for new policies (v2.0), use "tfp" instead of "acr").
           * To learn more about B2C tokens, visit https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
           */
          const policyName = ((event.payload.idTokenClaims["acr"] ?? "") as string).toLowerCase();
          switch (policyName) {
            case b2cPolicies.names.forgotPassword.toLowerCase():
              // User has just completed the forgot password journey
              redirectToAuthWithMessage("Your password has been reset. You can now sign in with your new password.");
              return;
            case b2cPolicies.names.editProfile.toLowerCase():
              // User has just completed the edit profile journey
              redirectToAuthWithMessage("Your profile has been updated.");
              return;
          }

          if (!("partnerId" in event.payload.idTokenClaims)) {
            /**
             * If user has just signed up, redirect to login or else they will not have
             * the correct partner claims.
             * This logic is slightly brittle and should be removed when possible.
             * TODO: Add missing claims to the sign up journey.
             */
            if ("email" in event.payload.idTokenClaims) {
              redirectToAuthWithMessage("You can now sign in to your account.");
              return;
            }

            // No partner ID provided
            redirectToAuthWithMessage("We need you to sign in again.");
            return;
          }
        }
      }

      if (event.eventType === EventType.LOGIN_SUCCESS && !isRedirecting) {
        const account = (event.payload as any)?.account
        if (account) {
          instance.setActiveAccount(account);
        }
      }

    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, []);

  return (
    <>
      {isHandledAuthError
        ? (<Loader show={true} />)
        : isError
          ? (<AuthError message={errorMessage} />)
          : isRedirecting
            ? (<Redirecting message={redirectMessage} />)
            : (
              <MsalAuthenticationTemplate
                interactionType={InteractionType.Redirect}
                authenticationRequest={authRequest}
                loadingComponent={() => (<Loader show />)}>
                <Content />
              </MsalAuthenticationTemplate>)
      }
    </>
  );
};

const App: React.FC = () => {
  return (
    isIE
      ? (
        <div className="text-center mx-2 my-5">
          <h1 className="text-primary">It looks like you're using an outdated browser</h1>
          <p className="text-secondary">For a safer and faster user experience use a modern browser like Chrome, Firefox, Safari, Opera, or Edge.</p>
        </div>
      )
      : bypassAuth
        ? (
          <Content />
        )
        : (
          <MsalProvider instance={msalInstance}>
            <Authorize />
          </MsalProvider>
        )
  );
};

export default App;
